import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config';
import './AdminLensOptions.css';

const AdminLensOptions = () => {
  const [lensOptions, setLensOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLens, setEditingLens] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    lens_type: 'standard',
    lens_category: 'eyeglass',
    duration: '',
    color_code: '',
    feature_type: '',
    range_type: '',
    is_active: 1
  });

  useEffect(() => {
    fetchLensOptions();
  }, []);

  const fetchLensOptions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/lens-options`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setLensOptions(data.data || []);
        } else {
          setError(data.message);
        }
      } else {
        setError('Failed to load lens options');
      }
    } catch (error) {
      console.error('Error fetching lens options:', error);
      setError('Error loading lens options');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      lens_type: 'standard',
      lens_category: 'eyeglass',
      duration: '',
      color_code: '',
      feature_type: '',
      range_type: '',
      is_active: 1
    });
    setEditingLens(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price) {
      alert('Name and price are required');
      return;
    }

    try {
      const url = editingLens 
        ? `${API_BASE_URL}/admin/lens-options/${editingLens.id}` 
        : `${API_BASE_URL}/admin/lens-options`;
      const method = editingLens ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(editingLens ? 'Lens option updated successfully' : 'Lens option created successfully');
        fetchLensOptions();
        resetForm();
      } else {
        alert(data.message || 'Failed to save lens option');
      }
    } catch (error) {
      console.error('Error saving lens option:', error);
      alert('Error saving lens option');
    }
  };

  const handleEdit = (lens) => {
    setFormData({
      name: lens.name,
      description: lens.description || '',
      price: lens.price,
      lens_type: lens.lens_type || 'standard',
      lens_category: lens.lens_category || 'eyeglass',
      duration: lens.duration || '',
      color_code: lens.color_code || '',
      feature_type: lens.feature_type || '',
      range_type: lens.range_type || '',
      is_active: lens.is_active
    });
    setEditingLens(lens);
    setShowAddForm(true);
  };

  const handleDelete = async (lensId, lensName) => {
    if (!window.confirm(`Are you sure you want to delete "${lensName}"?`)) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/lens-options/${lensId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert('Lens option deleted successfully');
        fetchLensOptions();
      } else {
        alert(data.message || 'Failed to delete lens option');
      }
    } catch (error) {
      console.error('Error deleting lens option:', error);
      alert('Error deleting lens option');
    }
  };

  const handleToggleStatus = async (lensId, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/lens-options/${lensId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ is_active: newStatus })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(`Lens option ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
        fetchLensOptions();
      } else {
        alert(data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status');
    }
  };

  const formatPrice = (price) => {
    if (!price) return '₹0.00';
    return `₹${parseFloat(price).toFixed(2)}`;
  };

  const getCategoryBadge = (category) => {
    const colors = {
      'eyeglass': '#3498db',
      'progressive': '#9b59b6',
      'contact': '#27ae60'
    };
    const labels = {
      'eyeglass': 'Eyeglass',
      'progressive': 'Progressive',
      'contact': 'Contact Lens'
    };
    return (
      <span className="category-badge" style={{ backgroundColor: colors[category] || '#95a5a6' }}>
        {labels[category] || category}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const colors = {
      'standard': '#3498db',
      'blue-cut': '#2980b9',
      'photochromic': '#f39c12',
      'progressive': '#9b59b6',
      'high-index': '#e74c3c',
      'toric': '#27ae60',
      'multifocal': '#16a085'
    };
    const labels = {
      'standard': 'Standard',
      'blue-cut': 'Blue Cut',
      'photochromic': 'Photochromic',
      'progressive': 'Progressive',
      'high-index': 'High Index',
      'toric': 'Toric',
      'multifocal': 'Multifocal'
    };
    return (
      <span className="type-badge" style={{ backgroundColor: colors[type] || '#95a5a6' }}>
        {labels[type] || type}
      </span>
    );
  };

  const filteredLenses = lensOptions.filter(lens => {
    if (filterCategory !== 'all' && lens.lens_category !== filterCategory) return false;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return lens.name?.toLowerCase().includes(searchLower) ||
             lens.description?.toLowerCase().includes(searchLower);
    }
    return true;
  });

  if (loading) {
    return <div className="admin-lens-loading">Loading lens options...</div>;
  }

  if (error) {
    return <div className="admin-lens-error">{error}</div>;
  }

  return (
    <div className="admin-lens-options">
      <div className="admin-lens-header">
        <h1>Lens Options Management</h1>
        <div className="admin-lens-actions">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search lens options..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </div>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="category-filter">
            <option value="all">All Categories</option>
            <option value="eyeglass">Eyeglass Lenses</option>
            <option value="progressive">Progressive Lenses</option>
            <option value="contact">Contact Lenses</option>
          </select>
          <button className="add-btn" onClick={() => setShowAddForm(true)}>
            + Add New Lens
          </button>
        </div>
      </div>

      <div className="admin-lens-stats">
        <div className="stat-card">
          <span className="stat-label">Total Lenses</span>
          <span className="stat-value">{lensOptions.length}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Eyeglass Lenses</span>
          <span className="stat-value">{lensOptions.filter(l => l.lens_category === 'eyeglass').length}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Progressive Lenses</span>
          <span className="stat-value">{lensOptions.filter(l => l.lens_category === 'progressive').length}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Contact Lenses</span>
          <span className="stat-value">{lensOptions.filter(l => l.lens_category === 'contact').length}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Active</span>
          <span className="stat-value">{lensOptions.filter(l => l.is_active === 1).length}</span>
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingLens ? 'Edit Lens Option' : 'Add New Lens Option'}</h2>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="lens-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Standard Lenses"
                  />
                </div>
                <div className="form-group">
                  <label>Price * (₹)</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Lens Category</label>
                  <select name="lens_category" value={formData.lens_category} onChange={handleInputChange}>
                    <option value="eyeglass">Eyeglass Lens</option>
                    <option value="progressive">Progressive Lens</option>
                    <option value="contact">Contact Lens</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Lens Type</label>
                  <select name="lens_type" value={formData.lens_type} onChange={handleInputChange}>
                    <option value="standard">Standard</option>
                    <option value="blue-cut">Blue Cut</option>
                    <option value="photochromic">Photochromic</option>
                    <option value="progressive">Progressive</option>
                    <option value="high-index">High Index</option>
                    <option value="toric">Toric</option>
                    <option value="multifocal">Multifocal</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Duration (for Contact Lenses)</label>
                  <select name="duration" value={formData.duration} onChange={handleInputChange}>
                    <option value="">Select Duration</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Color Code (for Contact Lenses)</label>
                  <input
                    type="text"
                    name="color_code"
                    value={formData.color_code}
                    onChange={handleInputChange}
                    placeholder="#FFFFFF"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Feature Type</label>
                  <select name="feature_type" value={formData.feature_type} onChange={handleInputChange}>
                    <option value="">Select Feature</option>
                    <option value="standard">Standard</option>
                    <option value="toric">Toric</option>
                    <option value="multifocal">Multifocal</option>
                    <option value="uv">UV Protection</option>
                    <option value="high_moisture">High Moisture</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Range Type (for Progressive)</label>
                  <select name="range_type" value={formData.range_type} onChange={handleInputChange}>
                    <option value="">Select Range</option>
                    <option value="standard">Standard</option>
                    <option value="wide">Wide</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Describe the lens features and benefits..."
                />
              </div>

              <div className="form-row">
                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active === 1}
                      onChange={handleInputChange}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn">
                  {editingLens ? 'Update Lens' : 'Create Lens'}
                </button>
                <button type="button" className="cancel-btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lens Options Table */}
      <div className="admin-lens-table-container">
        {filteredLenses.length === 0 ? (
          <div className="no-data">No lens options found</div>
        ) : (
          <table className="lens-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Category</th>
                <th>Type</th>
                <th>Price</th>
                <th>Duration/Color</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLenses.map(lens => (
                <tr key={lens.id}>
                  <td className="lens-id">#{lens.id}</td>
                  <td className="lens-name">
                    <div className="lens-name-cell">
                      <span className="lens-name-text">{lens.name}</span>
                      {lens.description && (
                        <span className="lens-desc-preview">{lens.description.substring(0, 50)}...</span>
                      )}
                    </div>
                  </td>
                  <td>{getCategoryBadge(lens.lens_category)}</td>
                  <td>{getTypeBadge(lens.lens_type)}</td>
                  <td className="lens-price">{formatPrice(lens.price)}</td>
                  <td className="lens-duration">
                    {lens.duration ? lens.duration.charAt(0).toUpperCase() + lens.duration.slice(1) : '-'}
                    {lens.color_code && (
                      <div className="color-preview">
                        <span className="color-dot" style={{ backgroundColor: lens.color_code }}></span>
                        <span className="color-code">{lens.color_code}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    <button 
                      className={`status-toggle ${lens.is_active === 1 ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus(lens.id, lens.is_active)}
                    >
                      {lens.is_active === 1 ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="actions-cell">
                    <button className="edit-btn" onClick={() => handleEdit(lens)} title="Edit">
                      ✏️
                    </button>
                    <button className="delete-btn" onClick={() => handleDelete(lens.id, lens.name)} title="Delete">
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminLensOptions;